import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Link,
  LinearProgress
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArticleIcon from '@mui/icons-material/Article';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TranslateIcon from '@mui/icons-material/Translate';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SegmentIcon from '@mui/icons-material/AllInclusive';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import GppGoodIcon from '@mui/icons-material/GppGood';
import standEancomConfig from '../utils/standEancomConfig';

// Helper function to group warnings and errors by type
const groupValidationItems = (items) => {
  return items.reduce((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {});
};

// STAND Compliance Dashboard Component
const StandComplianceDashboard = ({ validationResults, messageType }) => {
  if (!validationResults) return null;

  const { errors = [], warnings = [], stats = {} } = validationResults;
  
  // Calculate compliance metrics
  const totalChecks = stats.total || 0;
  const passedChecks = stats.passed || 0;
  const failedChecks = stats.failed || 0;
  const warningChecks = stats.warnings || 0;
  
  const complianceScore = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 0;
  
  // Categorize issues by STAND compliance areas
  const standCategories = {
    'syntax': { name: 'Syntax & Structure', issues: [], icon: <SegmentIcon /> },
    'mandatory': { name: 'Mandatory Elements', issues: [], icon: <ErrorIcon /> },
    'standard': { name: 'STAND Guidelines', issues: [], icon: <GppGoodIcon /> },
    'compliance': { name: 'Norwegian Compliance', issues: [], icon: <AssignmentTurnedInIcon /> },
    'format': { name: 'Format & Validation', issues: [], icon: <ArticleIcon /> }
  };
  
  // Categorize all issues
  [...errors, ...warnings].forEach(issue => {
    const category = standCategories[issue.type] || standCategories['format'];
    category.issues.push(issue);
  });
  
  return (
    <Box sx={{ mb: 3 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                <GppGoodIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                STAND EANCOM Compliance
              </Typography>
              <Typography variant="h3" color={complianceScore >= 90 ? 'success.main' : complianceScore >= 70 ? 'warning.main' : 'error.main'}>
                {complianceScore}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overall compliance score
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Compliance Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={complianceScore}
                  sx={{
                    height: 10,
                    borderRadius: 1,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: complianceScore >= 90 ? 'success.main' : complianceScore >= 70 ? 'warning.main' : 'error.main'
                    }
                  }}
                />
              </Box>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="success.main">{passedChecks}</Typography>
                    <Typography variant="caption">Passed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="warning.main">{warningChecks}</Typography>
                    <Typography variant="caption">Warnings</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="error.main">{failedChecks}</Typography>
                    <Typography variant="caption">Errors</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Grid container spacing={2}>
        {Object.entries(standCategories).map(([key, category]) => {
          if (category.issues.length === 0) return null;
          
          const errorCount = category.issues.filter(issue => errors.includes(issue)).length;
          const warningCount = category.issues.filter(issue => warnings.includes(issue)).length;
          
          return (
            <Grid item xs={12} md={6} lg={4} key={key}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {category.icon}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {category.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {errorCount > 0 && (
                      <Chip
                        icon={<ErrorIcon />}
                        label={`${errorCount} error${errorCount > 1 ? 's' : ''}`}
                        color="error"
                        size="small"
                      />
                    )}
                    {warningCount > 0 && (
                      <Chip
                        icon={<WarningIcon />}
                        label={`${warningCount} warning${warningCount > 1 ? 's' : ''}`}
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {category.issues.length} total issue{category.issues.length > 1 ? 's' : ''} found
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// Map of validation types to friendly names and icons
const validationTypeInfo = {
  'structure': { name: 'Structure', icon: <SegmentIcon /> },
  'sequence': { name: 'Sequence', icon: <SegmentIcon /> },
  'format': { name: 'Format', icon: <ArticleIcon /> },
  'code': { name: 'Code Values', icon: <QrCodeIcon /> },
  'business': { name: 'Business Rules', icon: <VerifiedUserIcon /> },
  'crossdock': { name: 'Crossdock', icon: <VerifiedUserIcon /> },
  'message': { name: 'Message Type', icon: <ArticleIcon /> },
  'group': { name: 'Segment Groups', icon: <SegmentIcon /> },
  'reference': { name: 'References', icon: <ArticleIcon /> },
  'charset': { name: 'Character Set', icon: <TranslateIcon /> },
  'version': { name: 'EDIFACT Version', icon: <InfoIcon /> },
  'bilateral': { name: 'Bilateral Agreements', icon: <HandshakeIcon /> },
  'compliance': { name: 'STAND Compliance', icon: <VerifiedUserIcon /> },
  'critical': { name: 'Critical Errors', icon: <ErrorIcon /> },
  'system': { name: 'System Errors', icon: <ErrorIcon /> },
  'mandatory': { name: 'Mandatory Errors', icon: <ErrorIcon /> }
};

const ValidationResults = ({ validationResults, isValidating, messageType, distributionType = null, autoValidate = true, onToggleAutoValidate = () => {}, onNavigateToLocation = () => {} }) => {
  if (isValidating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4, flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={40} color="primary" />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Validating document...
        </Typography>
      </Box>
    );
  }

  if (!validationResults) {
    return null;
  }

  const { valid, errors: originalErrors, warnings: originalWarnings, stats } = validationResults;
  const infoItems = validationResults.info || [];
  
  // Deduplicate errors and warnings that appear in both STAND and regular categories
  // We'll consider two errors to be duplicates if they have the same message
  const seenErrors = new Set();
  const errors = originalErrors.filter(error => {
    // Keep duplicates across different messages; only dedupe identical message+location+messageIndex
    const key = `${error.message}|${error.location || ''}|${error.messageIndex || ''}`;
    if (seenErrors.has(key)) return false;
    seenErrors.add(key);
    return true;
  });
  
  const seenWarnings = new Set();
  const warnings = originalWarnings.filter(warning => {
    const key = `${warning.message}|${warning.location || ''}|${warning.messageIndex || ''}`;
    if (seenWarnings.has(key)) return false;
    seenWarnings.add(key);
    return true;
  });
  
  // Update stats to reflect deduplicated counts
  const updatedStats = {
    ...stats,
    failed: errors.length,
    warnings: warnings.length,
    total: stats.passed + errors.length + warnings.length
  };
  
  // Get distribution type info if provided
  const distributionInfo = distributionType ? 
    standEancomConfig.DISTRIBUTION_TYPES[distributionType] : null;
  
  // Get message type info if available
  const messageInfo = messageType ? 
    standEancomConfig.MESSAGE_TYPES[messageType] : null;
    
  // Group warnings and errors by type
  const groupedWarnings = groupValidationItems(warnings);
  const groupedErrors = groupValidationItems(errors);
  
  // Treat ALL errors and warnings as part of STAND validation, since they all follow the same standard
  const hasStandSpecificWarnings = Object.keys(groupedWarnings).length > 0;
  const hasStandSpecificErrors = Object.keys(groupedErrors).length > 0;

  // All errors and warnings are now part of STAND validation
  const standErrorsCount = errors.length;
  const standWarningsCount = warnings.length;

  // Since we're showing all errors in the STAND section, there are no standard errors/warnings
  const standardErrorsCount = 0;
  const standardWarningsCount = 0;

  return (
    <Box sx={{ mt: 2 }}>
      {/* STAND Compliance Dashboard */}
      <StandComplianceDashboard 
        validationResults={validationResults} 
        messageType={messageType} 
      />
      
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          borderLeft: '4px solid', 
          borderColor: valid ? 'success.main' : errors.length > 0 ? 'error.main' : 'warning.main',
          background: valid
            ? 'linear-gradient(180deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.04) 100%)'
            : errors.length > 0
              ? 'linear-gradient(180deg, rgba(244, 67, 54, 0.08) 0%, rgba(244, 67, 54, 0.04) 100%)'
              : 'linear-gradient(180deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 152, 0, 0.04) 100%)',
          borderRadius: '4px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {valid ? (
            <CheckCircleIcon color="success" sx={{ mr: 2, fontSize: 28 }} />
          ) : errors.length > 0 ? (
            <ErrorIcon color="error" sx={{ mr: 2, fontSize: 28 }} />
          ) : (
            <WarningIcon color="warning" sx={{ mr: 2, fontSize: 28 }} />
          )}
          <Typography variant="h6">
            {valid ? 'Document is valid' : errors.length > 0 ? 'Document has validation errors' : 'Document has warnings'}
          </Typography>
        </Box>
        
        {(hasStandSpecificWarnings || hasStandSpecificErrors) && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <AutoAwesomeIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              Enhanced validation according to STAND guidelines applied
            </Typography>
          </Box>
        )}
        
        {errors.length > 0 && hasStandSpecificErrors && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <InfoIcon sx={{ mr: 1, color: 'info.main', fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              Please check the STAND-specific validations section for detailed error information
            </Typography>
          </Box>
        )}
      </Paper>

      {/* STAND Documentation Reference */}
      <Paper 
        elevation={0} 
        variant="outlined" 
        sx={{ 
          p: 2, 
          mb: 3, 
          borderRadius: '4px',
          backgroundColor: 'rgba(25, 118, 210, 0.04)'
        }}
      >
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <ArticleIcon sx={{ mr: 1 }} />
          Validation based on STAND guidelines
        </Typography>
        
        <Typography variant="body2" paragraph>
          This validation follows EANCOM implementation rules as defined by STAND for retail trade.
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip 
            icon={<InfoIcon />}
            label="STAND Guidelines" 
            component="a"
            href="https://stand.no/en/articles/197-implementation-of-edi-in-the-retail-trade/"
            target="_blank"
            rel="noopener"
            clickable
            sx={{ cursor: 'pointer' }}
          />
          {messageType && (
            <Chip 
              icon={<ArticleIcon />}
              label={`${messageType} Documentation`}
              color="primary"
              variant="outlined"
            />
          )}
          {distributionType && (
            <Chip 
              icon={<ArticleIcon />}
              label={`${distributionInfo?.name || distributionType}`}
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip label="Auto-validate" size="small" variant="outlined" />
          <Chip 
            label={autoValidate ? 'ON' : 'OFF'}
            color={autoValidate ? 'success' : 'default'}
            onClick={onToggleAutoValidate}
            variant={autoValidate ? 'filled' : 'outlined'}
            size="small"
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      </Paper>

      {/* Message and Distribution Type info */}
      {(messageInfo || distributionInfo) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {messageInfo && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    {messageType} - {messageInfo.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Required segments according to STAND:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {messageInfo.mandatorySegments.map(segment => (
                      <Chip 
                        key={segment}
                        label={segment} 
                        size="small"
                        color="primary"
                      />
                    ))}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Required dates:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {messageInfo.mandatoryDates.map(dateCode => (
                      <Chip 
                        key={dateCode}
                        label={`DTM+${dateCode} (${standEancomConfig.CODE_VALUES.DTM_QUALIFIERS[dateCode] || dateCode})`} 
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {distributionInfo && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ mr: 1, color: 'secondary.main' }} />
                    Distribution: {distributionInfo.name}
                  </Typography>
                  
                  <Typography variant="body2" paragraph>
                    {distributionInfo.description}
                  </Typography>
                  
                  {distributionInfo.additionalRules && (
                    <>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Special rules:
                      </Typography>
                      
                      <List dense disablePadding>
                        {distributionInfo.additionalRules.map((rule, index) => (
                          <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <InfoIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={rule}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Validation Stats */}
      {(errors.length > 0 || warnings.length > 0) && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Card sx={{ minWidth: 160, flexGrow: 1 }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" color="text.primary" sx={{ fontWeight: 500 }}>
                {updatedStats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Checks
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 160, flexGrow: 1 }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5, bgcolor: valid ? 'success.lighter' : 'white', '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 500 }}>
                {updatedStats.passed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Passed
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 160, flexGrow: 1 }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5, bgcolor: errors.length > 0 ? 'error.lighter' : 'white', '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" color="error.main" sx={{ fontWeight: 500 }}>
                {standErrorsCount + standardErrorsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Errors
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 160, flexGrow: 1 }}>
            <CardContent sx={{ textAlign: 'center', py: 1.5, bgcolor: warnings.length > 0 ? 'warning.lighter' : 'white', '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 500 }}>
                {standWarningsCount + standardWarningsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Warnings
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Informational guidance */}
      {infoItems.length > 0 && (
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 1.5, color: 'info.main' }} />
              <Typography variant="subtitle1">Guidance</Typography>
              <Chip label={`${infoItems.length}`} size="small" color="info" sx={{ ml: 1.5 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List disablePadding>
              {infoItems.map((it, idx) => (
                <Alert key={idx} severity="info" sx={{ mb: 1 }}>
                  {it.message}
                  {it.location && (
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      Location: {it.location}
                    </Typography>
                  )}
                </Alert>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* STAND-specific validations */}
      {(hasStandSpecificWarnings || hasStandSpecificErrors) && (
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="stand-specific-content"
            id="stand-specific-header"
            sx={{ backgroundColor: 'primary.lighter' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AutoAwesomeIcon sx={{ mr: 1.5, color: 'primary.main' }} />
              <Typography variant="subtitle1">STAND-specific validations</Typography>
              {hasStandSpecificErrors && (
                <Chip 
                  label={`${standErrorsCount} errors`} 
                  size="small" 
                  color="error" 
                  sx={{ ml: 1.5 }} 
                />
              )}
              {hasStandSpecificWarnings && (
                <Chip 
                  label={`${standWarningsCount} warnings`} 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List disablePadding>
              {/* Display all error types in the STAND validation section */}
                        {Object.entries(groupedErrors).map(([type, typeErrors]) => {
                const typeInfo = validationTypeInfo[type] || { name: type, icon: <InfoIcon /> };
                return (
                  <ListItem key={type} sx={{ display: 'block', pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {React.cloneElement(typeInfo.icon, { color: 'error' })}
                      <Typography variant="subtitle2" sx={{ ml: 1 }}>
                        {typeInfo.name} ({typeErrors.length})
                      </Typography>
                    </Box>
                              {typeErrors.map((item, index) => (
                      <Alert 
                        key={index} 
                        severity="error" 
                        sx={{ mb: 1 }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                          <Box>
                            {item.message}
                            {(item.messageIndex || item.messageRef) && (
                              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                                Msg #{item.messageIndex || '?'}{item.messageRef ? ` • Ref ${item.messageRef}` : ''}
                              </Typography>
                            )}
                          </Box>
                          {item.location && (
                            <Chip size="small" label="Go to" onClick={() => onNavigateToLocation(item.location)} color="info" variant="outlined" />
                          )}
                        </Box>
                        {(item.locationDisplay || item.location) && (
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Location: {item.locationDisplay || item.location}
                          </Typography>
                        )}
                        {/* Add helpful explanations for specific error types */}
                        {(type === 'group' || (type === 'mandatory' && item.message.includes('LIN'))) && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              <strong>What does this mean?</strong> This error relates to how segments should be grouped according to STAND guidelines. 
                              Groups ensure that related data elements are properly connected.
                            </Typography>
                          </Box>
                        )}
                        {(type === 'mandatory' && item.message.includes('LIN')) && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              <strong>What does this mean?</strong> According to STAND guidelines, the LIN (Line Item) segment 
                              is mandatory for INVOIC messages. Each invoice must contain at least one line item detailing 
                              what is being invoiced.
                            </Typography>
                          </Box>
                        )}
                        {type === 'crossdock' && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              <strong>What does this mean?</strong> Crossdock deliveries require specific data elements 
                              according to STAND guidelines for efficient logistics handling.
                            </Typography>
                          </Box>
                        )}
                      </Alert>
                    ))}
                  </ListItem>
                );
              })}

              {/* Display all warning types in the STAND validation section */}
                        {Object.entries(groupedWarnings).map(([type, typeWarnings]) => {
                const typeInfo = validationTypeInfo[type] || { name: type, icon: <InfoIcon /> };
                const severity = type === 'compliance' ? 'info' : 'warning';
                
                return (
                  <ListItem key={type} sx={{ display: 'block', pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {React.cloneElement(typeInfo.icon, { color: severity === 'info' ? 'primary' : 'warning' })}
                      <Typography variant="subtitle2" sx={{ ml: 1 }}>
                        {typeInfo.name} ({typeWarnings.length})
                      </Typography>
                    </Box>
                      {typeWarnings.map((item, index) => (
                      <Alert 
                        key={index} 
                        severity={severity}
                        icon={type === 'compliance' ? <InfoIcon /> : undefined}
                        sx={{ mb: 1 }}
                      >
                          {item.message}
                          {(item.messageIndex || item.messageRef) && (
                            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                              Msg #{item.messageIndex || '?'}{item.messageRef ? ` • Ref ${item.messageRef}` : ''}
                            </Typography>
                          )}
                          {(item.locationDisplay || item.location) && (
                            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                              Location: {item.locationDisplay || item.location}
                            </Typography>
                          )}
                        {item.compliance && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              size="small" 
                              variant="outlined" 
                              icon={<ArticleIcon />} 
                              label={`Message: ${item.compliance.messageType}`} 
                            />
                            <Chip 
                              size="small" 
                              variant="outlined" 
                              icon={<VerifiedUserIcon />} 
                              label={`STAND Version: ${item.compliance.standVersion}`} 
                            />
                          </Box>
                        )}
                        {item.agreements && item.agreements.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              Potential agreements required:
                            </Typography>
                            <List dense disablePadding>
                              {item.agreements.map((agreement, idx) => (
                                <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <InfoIcon fontSize="small" color="info" />
                                  </ListItemIcon>
                                  <ListItemText 
                                    primary={agreement}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </Alert>
                    ))}
                  </ListItem>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* STAND reference link */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ fontSize: 18, mr: 1, color: 'info.main' }} />
          This validation implements the EANCOM standards according to the STAND guidelines for Norwegian retail trade. For full reference, see the official <Link href="https://stand.no/wp-content/uploads/2018/08/198_Meldingsbeskrivelse_EANCOM.pdf" target="_blank" rel="noopener">EANCOM D.96A STAND documentation</Link>.
        </Typography>
      </Box>
    </Box>
  );
};

export default ValidationResults; 